import React from 'react';
import {
  Box, Typography, Button, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Select, MenuItem,
  ToggleButton, ToggleButtonGroup, Tooltip, Chip, Alert,
  FormControl,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RegenerateIcon,
  DragIndicator as DragIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { ChannelEntry, createManualChannel } from '../../utils/channelList';
import { MicType, SuppliedBy } from '../../constants/channelDefaults';

interface Props {
  channels: ChannelEntry[];
  onChange: (updated: ChannelEntry[]) => void;
  onRegenerate: () => void;   // triggers diff + confirm flow in parent
  hasStageDiff: boolean;      // true when stage has changed since last generate
}

const MIC_TYPES: MicType[] = ['Condenser', 'Dynamic', 'DI', 'Ribbon', 'Other'];

// Solid bg + white label — readable on both light and dark backgrounds
const MIC_TYPE_STYLE: Record<MicType, { bg: string; label: string }> = {
  Condenser: { bg: '#1565c0', label: '#ffffff' },
  Dynamic:   { bg: '#2e7d32', label: '#ffffff' },
  DI:        { bg: '#e65100', label: '#ffffff' },
  Ribbon:    { bg: '#6a1b9a', label: '#ffffff' },
  Other:     { bg: '#424242', label: '#ffffff' },
};

export default function ChannelListEditor({ channels, onChange, onRegenerate, hasStageDiff }: Props) {

  const update = (id: string, field: keyof ChannelEntry, value: unknown) => {
    onChange(channels.map(ch =>
      ch.id === id ? { ...ch, [field]: value, isOverridden: true } : ch,
    ));
  };

  const deleteRow = (id: string) => {
    const filtered = channels.filter(ch => ch.id !== id);
    onChange(filtered.map((ch, i) => ({ ...ch, channelNumber: i + 1 })));
  };

  const addManual = () => {
    const next = createManualChannel(channels.length + 1);
    onChange([...channels, next]);
  };

  const moveRow = (id: string, dir: -1 | 1) => {
    const idx = channels.findIndex(ch => ch.id === id);
    if (idx < 0) return;
    const next = [...channels];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next.map((ch, i) => ({ ...ch, channelNumber: i + 1 })));
  };

  const isOrphaned = (ch: ChannelEntry) => ch.channelName.startsWith('⚠');

  if (channels.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
        <Typography variant="h6" color="text.secondary">No channel list yet</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 420 }}>
          Place instruments on the stage plot, then click <strong>Generate from Stage</strong> to automatically create a channel list based on what's there.
        </Typography>
        <Button variant="contained" startIcon={<RegenerateIcon />} onClick={onRegenerate}>
          Generate from Stage
        </Button>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addManual} size="small">
          Or add a channel manually
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 1 }}>
          {channels.length} channel{channels.length !== 1 ? 's' : ''}
        </Typography>

        {hasStageDiff && (
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="small" />}
            sx={{ py: 0, px: 1, flex: 1, '& .MuiAlert-message': { py: 0.25 } }}
          >
            Stage has changed — some channels may be out of date.
          </Alert>
        )}

        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title="Re-derive channels from current stage layout. Your edits to existing channels are preserved.">
            <Button
              size="small"
              variant={hasStageDiff ? 'contained' : 'outlined'}
              color={hasStageDiff ? 'warning' : 'primary'}
              startIcon={<RegenerateIcon />}
              onClick={onRegenerate}
            >
              Regenerate from Stage
            </Button>
          </Tooltip>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addManual}>
            Add Custom Channel
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32 }} />
              <TableCell sx={{ width: 48, fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Instrument</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Channel Name</TableCell>
              <TableCell sx={{ width: 130, fontWeight: 700 }}>Mic Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Specific Mic</TableCell>
              <TableCell sx={{ width: 160, fontWeight: 700 }}>Supplied By</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
              <TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {channels.map((ch, idx) => (
              <TableRow
                key={ch.id}
                sx={{
                  backgroundColor: isOrphaned(ch) ? 'rgba(255,152,0,0.08)' : undefined,
                  '&:hover': { backgroundColor: isOrphaned(ch) ? 'rgba(255,152,0,0.14)' : 'action.hover' },
                }}
              >
                {/* Reorder */}
                <TableCell sx={{ p: 0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <IconButton size="small" onClick={() => moveRow(ch.id, -1)} disabled={idx === 0} sx={{ p: 0.25 }}>
                      <DragIcon sx={{ fontSize: 14, transform: 'rotate(90deg)' }} />
                    </IconButton>
                  </Box>
                </TableCell>

                {/* Channel number */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {ch.channelNumber}
                  </Typography>
                </TableCell>

                {/* Instrument */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isOrphaned(ch) && (
                      <Tooltip title="This element was removed from the stage">
                        <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      </Tooltip>
                    )}
                    <Typography variant="body2" sx={{ color: isOrphaned(ch) ? 'warning.dark' : 'text.primary' }}>
                      {ch.elementName}
                    </Typography>
                    {ch.isOverridden && !isOrphaned(ch) && (
                      <Chip label="edited" size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />
                    )}
                  </Box>
                </TableCell>

                {/* Channel name */}
                <TableCell>
                  <TextField
                    size="small"
                    variant="standard"
                    value={ch.channelName}
                    onChange={e => update(ch.id, 'channelName', e.target.value)}
                    inputProps={{ style: { fontWeight: 600 } }}
                    sx={{ minWidth: 100 }}
                  />
                </TableCell>

                {/* Mic type */}
                <TableCell>
                  <FormControl size="small" variant="standard" sx={{ minWidth: 110 }}>
                    <Select
                      value={ch.micType}
                      onChange={e => update(ch.id, 'micType', e.target.value)}
                      renderValue={val => (
                        <Chip
                          label={val}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: MIC_TYPE_STYLE[val as MicType].bg,
                            color: MIC_TYPE_STYLE[val as MicType].label,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    >
                      {MIC_TYPES.map(t => (
                        <MenuItem key={t} value={t}>
                          <Chip
                            label={t}
                            size="small"
                            sx={{ bgcolor: MIC_TYPE_STYLE[t].bg, color: MIC_TYPE_STYLE[t].label, fontWeight: 600 }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>

                {/* Specific mic */}
                <TableCell>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="e.g. SM57, DPA 4099"
                    value={ch.specificMic}
                    onChange={e => update(ch.id, 'specificMic', e.target.value)}
                    sx={{ minWidth: 110 }}
                  />
                </TableCell>

                {/* Supplied by */}
                <TableCell>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={ch.suppliedBy}
                    onChange={(_, v) => { if (v) update(ch.id, 'suppliedBy', v as SuppliedBy); }}
                    sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 0.75, fontSize: '0.7rem', textTransform: 'none' } }}
                  >
                    <ToggleButton value="venue">Venue</ToggleButton>
                    <ToggleButton value="artist">Artist</ToggleButton>
                    <ToggleButton value="either">Either</ToggleButton>
                  </ToggleButtonGroup>
                </TableCell>

                {/* Notes */}
                <TableCell>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Notes for sound engineer…"
                    value={ch.notes}
                    onChange={e => update(ch.id, 'notes', e.target.value)}
                    fullWidth
                    multiline
                    maxRows={2}
                    sx={{ minWidth: 160 }}
                  />
                </TableCell>

                {/* Delete */}
                <TableCell sx={{ p: 0.5 }}>
                  <IconButton size="small" onClick={() => deleteRow(ch.id)} color="error" sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer note */}
      <Box sx={{ px: 2, py: 0.75, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          Channel order is left-to-right by stage position. Drag ↕ rows to reorder. Changes autosave with the stage plot.
        </Typography>
      </Box>
    </Box>
  );
}
